export interface PricingRule {
  id: string;
  hall_id: string;
  name: string;
  start_time: string; // "06:00:00"
  end_time: string;   // "22:00:00"
  price_per_hour: number;
  is_peak: boolean;
  day_of_week: number[] | null; // 1=Mon, 7=Sun
  color: string;
  category: string;
  price_list_type?: string; // 'standard' | 'holiday' | 'special'
}

export interface SpecialPriceDate {
  id: string;
  organization_id: string;
  date: string; // "YYYY-MM-DD"
  price_list_type: string;
  label: string | null;
}

/**
 * Determine which price_list_type applies for a given date.
 * Returns the matching type from specialDates, or 'standard'.
 */
export function getPriceListType(
  date: Date,
  specialDates: SpecialPriceDate[]
): { type: string; label: string | null } {
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const match = specialDates.find((sd) => sd.date === dateStr);
  if (match) return { type: match.price_list_type, label: match.label };
  return { type: "standard", label: null };
}

/**
 * Calculate the total booking price by splitting the booking into 30-min blocks
 * and matching each block against the pricing rules for the given hall.
 *
 * Now supports special price lists: if a priceListType is provided, rules
 * with that type are used first, falling back to 'standard'.
 *
 * Returns { total, perHour, priceListType, priceListLabel }
 */
export function calculateBookingPrice(
  rules: PricingRule[],
  hallId: string,
  startTime: Date,
  durationMinutes: number,
  category: string = "standard",
  specialDates: SpecialPriceDate[] = []
): { total: number; perHour: number | null; isPeak: boolean; priceListType: string; priceListLabel: string | null } {
  const { type: priceListType, label: priceListLabel } = getPriceListType(startTime, specialDates);

  // Try to find rules for the determined price list type
  let hallRules = rules.filter(
    (r) => r.hall_id === hallId && r.category === category && (r.price_list_type ?? "standard") === priceListType
  );

  // Fallback to standard price list type if no rules for special type
  if (hallRules.length === 0 && priceListType !== "standard") {
    hallRules = rules.filter(
      (r) => r.hall_id === hallId && r.category === category && (r.price_list_type ?? "standard") === "standard"
    );
  }

  // Fallback to standard category if no rules for the given category
  if (hallRules.length === 0 && category !== "standard") {
    hallRules = rules.filter(
      (r) => r.hall_id === hallId && r.category === "standard" && (r.price_list_type ?? "standard") === priceListType
    );
    if (hallRules.length === 0 && priceListType !== "standard") {
      hallRules = rules.filter(
        (r) => r.hall_id === hallId && r.category === "standard" && (r.price_list_type ?? "standard") === "standard"
      );
    }
  }

  if (hallRules.length === 0) return { total: 0, perHour: null, isPeak: false, priceListType: "standard", priceListLabel: null };

  const blockCount = Math.ceil(durationMinutes / 30);
  let total = 0;
  let matchedBlocks = 0;
  let hasPeak = false;

  // JS getDay(): 0=Sun, convert to 1=Mon...7=Sun
  const jsDay = startTime.getDay();
  const isoDay = jsDay === 0 ? 7 : jsDay;

  for (let i = 0; i < blockCount; i++) {
    const blockStart = new Date(startTime.getTime() + i * 30 * 60_000);
    const blockTimeStr = timeToStr(blockStart);

    // Find matching rule: prefer peak rules over off-peak
    const matching = hallRules.filter((r) => {
      // Check day_of_week (null means all days)
      if (r.day_of_week && r.day_of_week.length > 0 && !r.day_of_week.includes(isoDay)) {
        return false;
      }
      // Check time range
      return blockTimeStr >= r.start_time.slice(0, 5) && blockTimeStr < r.end_time.slice(0, 5);
    });

    if (matching.length > 0) {
      // Prefer peak rule if multiple match
      const rule = matching.find((r) => r.is_peak) ?? matching[0];
      if (rule.is_peak) hasPeak = true;
      total += rule.price_per_hour / 2; // 30-min block = half hour
      matchedBlocks++;
    }
  }

  const perHour = matchedBlocks > 0 ? Math.round(total / (matchedBlocks * 0.5)) : null;
  return {
    total: Math.round(total),
    perHour,
    isPeak: hasPeak,
    priceListType: hallRules.length > 0 && priceListType !== "standard" ? priceListType : "standard",
    priceListLabel: hallRules.length > 0 && priceListType !== "standard" ? priceListLabel : null,
  };
}

function timeToStr(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// ─── Bug B fix #3: per-seat pricing for PRIVATE bookings ────────────────────
//
// Operator-locked business logic (see boply-bug-b-fix3-per-seat-prompt.md):
// A private booking covers all seats of the court (N = max_players). The
// booker pays:
//   - Their OWN seat at their category rate (member → 0 kr at AP Nacka)
//   - PLUS each invited player's seat at THAT player's category rate
//   - PLUS every empty/uninvited seat at STANDARD rate
//
// Example for a 490 kr / 4-seat court, member booker, 0 invited:
//   - booker seat: 0 kr (member)
//   - 3 empty seats: 3 × (490/4) = 367.50 kr
//   - "Du betalar" ≈ 367.50 kr (NOT 0 kr, NOT 490 kr)
//
// This mirrors the open-match per-participant share model (proven correct
// in audit-reports/investigations/open-match-share-zero-discovery.md).
//
// Standard-booker no-op (F5 safety property): for a standard booker with
// all-standard invited (or 0 invited) and all-empty seats also at standard,
// the formula reproduces the original full court_standard_price EXACTLY
// (the all-standard guard returns courtPriceMinorForCategory("standard")).

export interface PrivateSeatPricingArgs {
  /** Total seats for this court (court.max_players, typically 2 or 4) */
  seatCount: number;
  /** Booker's category slug (sync-resolved by caller; "standard" if unknown) */
  bookerCategorySlug: string;
  /** Each invited player's category slug (length ≤ seatCount-1) */
  invitedCategorySlugs: string[];
  /**
   * Pure synchronous lookup of court price in MINOR units (öre) for a given
   * category slug. Callers typically close over `calculateBookingPrice(...)`:
   *   (cat) => Math.round(calculateBookingPrice(rules, hall, start, duration, cat, sd).total * 100)
   */
  courtPriceMinorForCategory: (categorySlug: string) => number;
}

export interface PrivateSeatPricingResult {
  /** Total price for this slot in MINOR units (öre) — canonical, exact */
  totalMinor: number;
  /** Standard per-seat in öre (court_standard_minor / seatCount, Math.round) */
  perSeatStdMinor: number;
  /** Booker's seat contribution in öre */
  bookerSeatMinor: number;
  /** Sum of invited players' seat contributions in öre */
  invitedSeatsMinor: number;
  /** Number of empty/unfilled seats */
  emptySeatCount: number;
  /** Sum of empty seats in öre (= emptySeatCount × perSeatStdMinor) */
  emptySeatsMinor: number;
}

/**
 * Calculate per-seat private-booking price for a single court/slot.
 *
 * @see PrivateSeatPricingArgs for parameters. Returns öre-exact total in
 * `totalMinor`. Caller divides by 100 for legacy `price_total` (kronor)
 * column but should prefer writing `price_total_minor` explicitly to
 * preserve öre precision (avoids dual_write_bookings_minor rounding drift).
 */
export function calculatePrivateBookingPerSeatPrice(
  args: PrivateSeatPricingArgs,
): PrivateSeatPricingResult {
  const { seatCount, bookerCategorySlug, invitedCategorySlugs, courtPriceMinorForCategory } = args;

  if (seatCount <= 0) {
    return {
      totalMinor: 0,
      perSeatStdMinor: 0,
      bookerSeatMinor: 0,
      invitedSeatsMinor: 0,
      emptySeatCount: 0,
      emptySeatsMinor: 0,
    };
  }

  // Standard per-seat (also used for empty seats)
  const stdCourtMinor = courtPriceMinorForCategory("standard");
  const perSeatStdMinor = Math.round(stdCourtMinor / seatCount);

  // F5 SAFETY-NET: when every occupant is 'standard', return the court
  // standard total EXACTLY. This guards against per-seat rounding drift
  // for seatCounts that don't evenly divide the court öre amount (e.g.
  // seatCount=3, court=10000 → per-seat=3333, 3×3333=9999 → 1 öre drift).
  // For padel courts (seatCount ∈ {2,4}, court=multiple of 100), per-seat
  // math is always exact and this guard is redundant; for non-padel court
  // types or future capacity changes, the guard preserves no-op.
  const allStandard =
    bookerCategorySlug === "standard" &&
    invitedCategorySlugs.every((c) => c === "standard");

  // Booker's seat: their category court price / seats
  const bookerCourtMinor = courtPriceMinorForCategory(bookerCategorySlug);
  const bookerSeatMinor = Math.round(bookerCourtMinor / seatCount);

  // Each invited player's seat: their category court price / seats
  const invitedSeatsMinor = invitedCategorySlugs.reduce((sum, cat) => {
    const courtMinor = courtPriceMinorForCategory(cat);
    return sum + Math.round(courtMinor / seatCount);
  }, 0);

  // Empty seats = seatCount - 1 (booker always occupies 1) - invited count.
  // Clamp to 0 in case invited.length > seatCount-1 (defensive — caller
  // should prevent over-invite but math stays non-negative either way).
  const emptySeatCount = Math.max(0, seatCount - 1 - invitedCategorySlugs.length);
  const emptySeatsMinor = emptySeatCount * perSeatStdMinor;

  const computedTotal = bookerSeatMinor + invitedSeatsMinor + emptySeatsMinor;

  // Apply the all-standard exact-no-op guard
  const totalMinor = allStandard ? stdCourtMinor : computedTotal;

  return {
    totalMinor,
    perSeatStdMinor,
    bookerSeatMinor,
    invitedSeatsMinor,
    emptySeatCount,
    emptySeatsMinor,
  };
}

// ─── Lager 3b.1 + Path B+ — Pris-resolver för boknings-flödet ───────────────
//
// Konsoliderar mål-pris-beräkningen för ALLA bokningsflöden (create, move,
// reschedule): EN källa för display, finalPrice, RPC-payload och charge-
// belopp. Pure logic, kontext-agnostisk per Lesson 42 (renamed från
// resolveMoveTargetPrice 2026-05-24 — namnet "Move" ljög om scope och
// förhindrade AdminScheduleGrid + RescheduleDialog från att hitta helpern,
// vilket orsakade M1+M3-buggar — se Path B+ Fas 0-rapport).
//
// Branch-logik (canonical, matchar `routeBookingClick`-precedensen i
// AdminScheduleGrid):
//   - bookingKind='open_match' → flat standard-court-pris (matchar open-
//     match-modellen där base_court_price_minor är referens, oavsett
//     deltagarens kategori)
//   - bookingKind='private'    → per-seat-summerat pris via
//     `calculatePrivateBookingPerSeatPrice` (F5 all-standard guard
//     preserverar exakt court-std för bokningar utan medlemmar)
//
// Pure: ingen Supabase, ingen React, ingen Date.now. Caller resolvar
// bookerCategorySlug + invitedCategorySlugs (async) och passar in.
//
// Använd för: create-flow (bookingConfirmation.ts, AdminScheduleGrid),
// move-flow (MoveBookingDialog, RescheduleDialog), reschedule-flow.

export type BookingPriceModel = "standard" | "per_seat";

export interface BookingTargetSlot {
  hallId: string;
  startTime: Date;
  durationMinutes: number;
  /** courts.max_players — oftast 4 för padel, 2 för t.ex. enkel. */
  seatCount: number;
  pricingRules: PricingRule[];
  specialDates?: SpecialPriceDate[];
}

export interface ResolveBookingTargetPriceArgs {
  /** Binär från bookingKindForPricing(booking) = `is_open_match || activity_id ? 'open_match' : 'private'`. */
  bookingKind: "open_match" | "private";
  targetSlot: BookingTargetSlot;
  /** Krävs IFF bookingKind='private'. Resolva async via getPlayerCategory. */
  bookerCategorySlug?: string;
  /** Krävs IFF bookingKind='private'. Tomma array = inga inbjudna. */
  invitedCategorySlugs?: string[];
}

export interface ResolveBookingTargetPriceResult {
  model: BookingPriceModel;
  /** Mål-pris i öre (öre-exakt; ingen kr→öre→kr-flip). */
  totalMinor: number;
  /** Standard court per-hour i öre (referens för bookings.price_per_hour_minor). */
  perHourMinor: number | null;
  /** Breakdown för display + audit metadata. */
  breakdown:
    | {
        type: "standard";
        courtTotalMinor: number;
      }
    | {
        type: "per_seat";
        seatCount: number;
        perSeatStdMinor: number;
        bookerSeatMinor: number;
        invitedSeatsMinor: number;
        emptySeatCount: number;
        emptySeatsMinor: number;
      };
}

/**
 * Resolve the target-slot price for a booking (create, move, or reschedule),
 * branching on booking kind so PRIVATE-bokningar får per-seat-summering och
 * open-match-bokningar får flat standard-court-pris.
 *
 * Kontext-agnostisk per Lesson 42 — använd för ALLA write-paths som behöver
 * canonical target-pris (create-time INSERT, move/reschedule UPDATE, charge-
 * booking-difference). Inga parallella prisuträkningar — denna är single
 * source of truth (Lesson 38).
 */
export function resolveBookingTargetPrice(
  args: ResolveBookingTargetPriceArgs,
): ResolveBookingTargetPriceResult {
  const { bookingKind, targetSlot } = args;
  const {
    hallId,
    startTime,
    durationMinutes,
    seatCount,
    pricingRules,
    specialDates = [],
  } = targetSlot;

  // Standard court price: alltid beräknat (används för perHourMinor
  // + open-match-grenen + per-seat closure). En enda call-site.
  const stdPrice = calculateBookingPrice(
    pricingRules,
    hallId,
    startTime,
    durationMinutes,
    "standard",
    specialDates,
  );
  const stdCourtMinor = Math.round((stdPrice.total ?? 0) * 100);
  const perHourMinor =
    stdPrice.perHour != null ? Math.round(stdPrice.perHour * 100) : null;

  if (bookingKind === "open_match") {
    return {
      model: "standard",
      totalMinor: stdCourtMinor,
      perHourMinor,
      breakdown: { type: "standard", courtTotalMinor: stdCourtMinor },
    };
  }

  // PRIVATE — kräver booker + invited slugs. Defaultar till "standard"
  // för att F5-guarden i per-seat-helpern ska kunna fire vid full
  // standard-konfiguration (returnerar då stdCourtMinor exakt).
  const bookerSlug = args.bookerCategorySlug ?? "standard";
  const invitedSlugs = args.invitedCategorySlugs ?? [];

  const courtPriceMinorForCategory = (cat: string): number => {
    if (cat === "standard") return stdCourtMinor; // återanvänd — undvik extra calc
    const p = calculateBookingPrice(
      pricingRules,
      hallId,
      startTime,
      durationMinutes,
      cat,
      specialDates,
    );
    return Math.round((p.total ?? 0) * 100);
  };

  const perSeat = calculatePrivateBookingPerSeatPrice({
    seatCount,
    bookerCategorySlug: bookerSlug,
    invitedCategorySlugs: invitedSlugs,
    courtPriceMinorForCategory,
  });

  return {
    model: "per_seat",
    totalMinor: perSeat.totalMinor,
    perHourMinor,
    breakdown: {
      type: "per_seat",
      seatCount,
      perSeatStdMinor: perSeat.perSeatStdMinor,
      bookerSeatMinor: perSeat.bookerSeatMinor,
      invitedSeatsMinor: perSeat.invitedSeatsMinor,
      emptySeatCount: perSeat.emptySeatCount,
      emptySeatsMinor: perSeat.emptySeatsMinor,
    },
  };
}

/**
 * Helper: branch booking kind for pricing (binär PRIVATE vs open-match).
 * Matchar precedensen i AdminScheduleGrid.routeBookingClick.
 */
export function bookingKindForPricing(b: {
  is_open_match: boolean | null;
  activity_id?: string | null;
}): "open_match" | "private" {
  if (b.is_open_match || b.activity_id) return "open_match";
  return "private";
}
